import type { Editor } from "@tiptap/react";
import type { MessageInputRef } from "./MessageInput";

import React, { forwardRef, useCallback, useRef, useState } from "react";

import { useCurrentChatContext } from "@editor/contexts/CurrentChatContext";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useAIStreaming } from "@editor/providers/AIStreamingProvider";

import {
  useIsChatLoading,
  useMessagesForChat,
  useToolCallProgressMappingForChat,
} from "@/features/react-editor/components/chat/store/ChatStoreContext";
import EmptyModifierState from "@/features/react-editor/components/modifiers/base/EmptyModifier";
import Modifiers from "@/features/react-editor/components/modifiers/Modifiers";
import {
  useIsAnyElementSelected,
  useModifiersStore,
  useModifiersStoreActions,
} from "@/features/react-editor/components/modifiers/store/ModifiersStoreContext";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import twMerge from "@replo/design-system/utils/twMerge";
import { useSearchParams } from "react-router-dom";

import { useChatId } from "./hooks/useChatId";
import { useQuickPrompt } from "./hooks/useQuickPrompt";
import { MessageInput } from "./MessageInput";
import MessageLimitWrapper from "./MessageLimitWrapper";
import { MessageList } from "./MessageList";
import { ScrollToBottomIndicator } from "./ScrollToBottomIndicator";
import { useAutoScroll } from "./use-auto-scroll";

interface ScrollState {
  isScrolledToBottom: boolean;
  forceScrollToBottom: () => void;
}

interface ChatProps {
  handleSubmit: (e?: React.FormEvent) => void;
  className?: string;
  stop?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  editor: Editor | null;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onForceScrollToTop?: React.MutableRefObject<(() => void) | null>;
  isLoading?: boolean;
  toggleDisplayMode?: () => void;
}

export function Chat({
  handleSubmit,
  stop,
  className,
  editor,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  onForceScrollToTop,
  toggleDisplayMode,
}: ChatProps) {
  const messageInputRef = useRef<MessageInputRef>(null);
  const { status } = useAIStreaming();
  const { user } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const chatId = searchParams.get("chatId");
  const isChatLoading = useIsChatLoading(chatId);
  const messages = useMessagesForChat(chatId);
  const { chat, files, setFiles } = useCurrentChatContext();
  const ownerId = chat?.userId;

  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const forceScrollToBottomRef = useRef<(() => void) | null>(null);

  const lastMessage = messages.at(-1);
  const isTyping = lastMessage?.role === "user" && isChatLoading;
  const canEdit = !ownerId || ownerId === user?.id;

  const { displayMode } = useModifiersStore();
  const modifierActions = useModifiersStoreActions();
  const isAnyElementSelected = useIsAnyElementSelected();

  const handleQuickPrompt = useQuickPrompt({
    editor,
    onSelectUploadImage: () => {
      void messageInputRef.current?.triggerFileUpload();
    },
    onSelectProduct: () => {
      void messageInputRef.current?.triggerContextMenu();
    },
  });

  const handleScrollStateChange = useCallback((state: ScrollState) => {
    setIsScrolledToBottom(state.isScrolledToBottom);
    forceScrollToBottomRef.current = state.forceScrollToBottom;
  }, []);

  const showScrollIndicator = !isScrolledToBottom;

  return (
    <ChatContainer className={twMerge("h-full", className)}>
      <div className="relative flex min-h-0 sm:pb-3 h-full w-full">
        <ChatMessages
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onForceScrollToTop={onForceScrollToTop}
          onScrollStateChange={handleScrollStateChange}
        >
          <MessageList
            isTyping={isTyping}
            editor={editor}
            onQuickPrompt={handleQuickPrompt}
          />
        </ChatMessages>
        {/* Chat gradient - creates fade effect at the top into content area */}
        <div className="absolute inset-x-0 top-0 pointer-events-none">
          <div className="h-5 w-full bg-gradient-to-b from-white via-white/90 to-transparent" />
        </div>
        {/* Chat gradient - creates fade effect into input area */}
        <div className="absolute inset-x-0 bottom-0 pointer-events-none">
          <div className="h-5 w-full bg-gradient-to-t from-white via-white/90 to-transparent" />
        </div>
        <ScrollToBottomIndicator
          isVisible={showScrollIndicator}
          onScrollToBottom={() => forceScrollToBottomRef.current?.()}
        />
        {canEdit && displayMode === "select" && (
          <div className="absolute bottom-2 left-0 right-0 top-2 flex items-end pointer-events-none">
            <div className="flex flex-col pointer-events-auto max-h-full max-w-full w-full">
              {isAnyElementSelected ? <Modifiers /> : <EmptyModifierState />}
            </div>
          </div>
        )}
      </div>
      {canEdit && (
        <>
          {status === "generating" && (
            <div className="flex items-center gap-2 text-foreground mb-2">
              <img
                src="/images/templates/ai-loader.svg"
                alt="AI is thinking"
                className="w-6 h-6 animate-spin"
              />
              <span className="typ-body-base">Applying changes...</span>
            </div>
          )}

          <div className="relative min-w-0">
            <form
              onSubmit={(e) => {
                void handleSubmit(e);
              }}
            >
              <div className="relative">
                <MessageLimitWrapper>
                  <MessageInput
                    ref={messageInputRef}
                    files={files}
                    setFiles={setFiles}
                    allowAttachments={true}
                    stop={stop}
                    enableInterrupt={true}
                    editor={editor}
                    isChatLoading={isChatLoading}
                    displayMode={displayMode}
                    toggleDisplayMode={
                      toggleDisplayMode ?? modifierActions.toggleDisplayMode
                    }
                    editorClassName="max-h-[120px] "
                  />
                </MessageLimitWrapper>
              </div>
            </form>
          </div>
        </>
      )}
    </ChatContainer>
  );
}

function ChatMessages({
  children,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  onForceScrollToTop,
  onScrollStateChange,
}: React.PropsWithChildren & {
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onForceScrollToTop?: React.MutableRefObject<(() => void) | null>;
  onScrollStateChange?: (state: ScrollState) => void;
}) {
  const chatId = useChatId();
  const messages = useMessagesForChat(chatId);
  const toolCallProgressMapping = useToolCallProgressMappingForChat(chatId);

  const {
    containerRef,
    forceScrollToBottom,
    handleScroll,
    handleTouchStart,
    isScrolledToBottom,
  } = useAutoScroll([messages, toolCallProgressMapping], chatId);

  // Expose forceScrollToBottom function to parent
  React.useEffect(() => {
    if (onForceScrollToTop) {
      onForceScrollToTop.current = forceScrollToBottom;
    }
  }, [forceScrollToBottom, onForceScrollToTop]);

  // Communicate scroll state to parent
  React.useEffect(() => {
    if (onScrollStateChange) {
      onScrollStateChange({ isScrolledToBottom, forceScrollToBottom });
    }
  }, [isScrolledToBottom, forceScrollToBottom, onScrollStateChange]);

  // NOTE (Bobby: 2025-07-13): Handling a lot of the infinite scroll logic custom since the InfiniteScroll component
  // was not working inverse as wanted. Pagination wasn't working with the inverse order
  const previousScrollHeightRef = useRef<number | null>(null);
  const isLoadingMoreRef = useRef(false);

  const handleScrollWithInfiniteLoad = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      handleScroll();

      // Check if user has scrolled to the top and we have more messages to load
      const target = e.target as HTMLDivElement;
      const scrollTop = target.scrollTop;

      if (
        scrollTop === 0 &&
        hasNextPage &&
        !isFetchingNextPage &&
        fetchNextPage
      ) {
        // Store current scroll height before loading more messages
        previousScrollHeightRef.current = target.scrollHeight;
        isLoadingMoreRef.current = true;
        fetchNextPage();
      }
    },
    [handleScroll, hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  // Restore scroll position after new messages are loaded
  React.useEffect(() => {
    if (
      !isFetchingNextPage &&
      isLoadingMoreRef.current &&
      containerRef.current &&
      previousScrollHeightRef.current !== null
    ) {
      // Use requestAnimationFrame to ensure DOM is fully updated
      requestAnimationFrame(() => {
        if (containerRef.current && previousScrollHeightRef.current !== null) {
          const newScrollHeight = containerRef.current.scrollHeight;
          const heightDifference =
            newScrollHeight - previousScrollHeightRef.current;

          // Restore scroll position to maintain visual position
          containerRef.current.scrollTop = heightDifference;
          previousScrollHeightRef.current = null;
          isLoadingMoreRef.current = false;
        }
      });
    }
  }, [isFetchingNextPage, containerRef]);

  return (
    <div
      className="grid grid-cols-1 overflow-y-auto sm:pb-10 pt-3 [scrollbar-width:none] h-full max-h-full min-h-0 w-full"
      ref={containerRef}
      onScroll={handleScrollWithInfiniteLoad}
      onTouchStart={handleTouchStart}
    >
      <div className="w-full [grid-column:1/1] [grid-row:1/1] h-full min-h-0">
        {isFetchingNextPage && (
          <div className="flex items-center justify-center py-2">
            <Spinner variant="secondary" size={16} />
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

const ChatContainer = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={twMerge(
        "grid max-h-full w-full grid-rows-[1fr_auto] h-full overflow-hidden min-h-0",
        className,
      )}
      {...props}
    />
  );
});
ChatContainer.displayName = "ChatContainer";
